import React from 'react';
import { TechLayout } from '../../components/tech/TechLayout';
import { H3, Lead, Purpose, TCap, Fig, SpecTable, DataTable } from '../../components/tech/TechUI';
import { ZoomImage } from '../../components/tech/Zoom';
import { T } from '../../data/techSections';

const RIG = [['LF', 'Left front', 'at the hands'], ['LE', 'Left eye', 'straight down'], ['L', 'Left side', 'the room'], ['RF', 'Right front', 'at the hands'], ['RE', 'Right eye', 'straight down'], ['R', 'Right side', 'the room']];

const CAMERAS = [
  ['Cameras', 'Six, in three stereo pairs. Each pair sees the same scene from two points, which gives depth.'],
  ['Front pair', 'Angled down at the hands and the work surface'],
  ['Eye pair', 'Straight down past the chin: both arms and hands, even when they leave the front view'],
  ['Side pair', 'Out to the left and right: the room, fixtures and people nearby'],
  ['Resolution', 'Full HD, 1920 × 1080, on every camera'],
  ['Frame rate', '30 frames per second on every camera'],
  ['Lens', 'Wide-angle fisheye'],
  ['Format', 'HEVC (H.265) in MP4, one file per camera'],
];

const IMU = [
  ['IMUs', 'Three, at the left, middle and right of the cap'],
  ['What it measures', 'Gyroscope: how fast the head turns. Accelerometer: how it speeds up and slows down, and which way is down.'],
  ['Sampling', 'About 200 readings per second from each sensor'],
  ['Why a lab needs it', 'Cameras lose track in fast head turns and motion blur; the IMU keeps measuring. Combined with the cameras it gives the path of the head through the space, so hand movements can be placed in the room, not just in the image.'],
];

const SYNC = [
  ['Clock', 'One cap clock for every camera and sensor'],
  ['Video', 'Each file carries its start time on that clock'],
  ['IMU', 'Every reading is time-stamped on the same clock'],
  ['One recording', 'Six MP4 files, three IMU logs and one magnetometer log'],
];

const Pair = ({ v, p }) => <>{v}<span className="block text-[10px] font-sans text-white/35 mt-0.5">{p}</span></>;
const Win = ({ v }) => <b className="text-[#FF7A00]">{v}</b>;

const COMPARE = [
  ['Hand-object actions seen clearly', <Pair v="37.5%" p="eye pair" />, <Win v="93.1%" />, 'An action counts when a camera in the set sees both the hand and the object clearly; judged per camera for each labelled action'],
  ['Frames with both hands in view', <Pair v="25.8%" p="eye pair" />, <Win v="72.5%" />, 'WiLoR hand detector at 50% confidence or more, in any camera of the set'],
  ['Frames with at least one hand in view', <Pair v="70.1%" p="eye pair" />, <Win v="95.5%" />, 'As above, either hand'],
  ['SLAM frames tracked (one camera)', <Pair v="80.3%" p="forward camera" />, <Win v="96.9%" />, 'ORB-SLAM3 monocular, same settings. Forward camera 80.3%, eye camera 82.8%, side camera (JanData Cap) 96.9%. The forward camera broke its map in 2 of 4 recordings, the eye camera in 1, the side camera in none'],
  ['Tracking lost in fast head turns', <Pair v="11.1%" p="forward pair" />, <Win v="2.8%" />, 'Feature tracks frame to frame; lost when no camera in the set keeps its tracks. Fast means the gyroscope reads over 100°/s'],
  ['Head-rotation estimate fails in fast turns', <Pair v="14.7%" p="eye pair" />, <Win v="6.9%" />, 'Head rotation from the cameras, checked against the gyroscope, in turns over 100°/s'],
];

export default function CapturePage() {
  return (
    <TechLayout slug="capture" lead={<Lead>Each worker wears a cap with six cameras and three motion sensors, all running on one clock. Together they see both hands, the work in front of them and the room around them, and they record how the head moves.</Lead>}>
      <Fig cap="The same instant from all six cameras during PCB soldering.">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="rig-grid">
          {RIG.map(([f, n, d]) => (
            <figure key={f} className="m-0">
              <ZoomImage src={T(`rig/${f}.jpg`)} alt={`${n} camera`} testid={`rig-${f}`} />
              <figcaption className="text-xs text-white/45 mt-1.5"><b className="text-white/80">{n}</b> · {d}</figcaption>
            </figure>
          ))}
        </div>
      </Fig>

      <H3 id="cameras">Cameras</H3>
      <TCap title="Cameras.">What each camera pair sees and how every camera records.</TCap>
      <SpecTable rows={CAMERAS} testid="table-cameras" />

      <H3 id="motion-sensors-imu">Motion sensors (IMU)</H3>
      <TCap title="Motion sensors.">What the IMUs measure and why a robotics lab wants them.</TCap>
      <SpecTable rows={IMU} testid="table-imu" />

      <H3 id="synchronisation-and-files">Synchronisation and files</H3>
      <TCap title="Synchronisation and files.">How the streams line up, and what one recording contains.</TCap>
      <SpecTable rows={SYNC} testid="table-sync" />

      <H3 id="advantages-over-a-stereo-rig">Advantages over a stereo rig</H3>
      <Lead>Most egocentric data comes from a single stereo pair. To show what our data adds, we took our own recordings, kept only one of the cap's stereo pairs, and ran each check both ways. For each measure, the stereo column shows the weaker of the forward and the downward (eye) pair, named in the table.</Lead>
      <TCap title="One stereo pair against the full JanData Cap,">on the same recordings. Higher is better except where marked.</TCap>
      <DataTable head={['Measure', 'One stereo pair', 'JanData Cap', 'How it is measured']} right={[1, 2]} rows={COMPARE} testid="table-compare" min={720} />

      <H3 id="what-a-forward-pair-misses">What a forward pair misses</H3>
      <Fig cap="Click to expand. Two labelled hand actions, placing a circuit board in a rack and picking up a crate, at the same instant in all six cameras. Neither forward camera sees them clearly; the downward eye pair does.">
        <ZoomImage src={T('compare-missed.jpg')} alt="Hand actions seen only by the downward cameras" testid="figure-compare-missed" />
      </Fig>

      <H3 id="what-the-imu-adds">What the IMU adds</H3>
      <Purpose>With the cap's IMU data, ORB-SLAM3 recovered the head path in metres and the direction of gravity. Depth Anything V2, run separately on the same keyframes, gave the same scale. A stereo pair alone gives scale from its baseline, but not gravity, and it loses track in the fast turns the IMU still measures.</Purpose>
    </TechLayout>
  );
}
